/**
 * Cliente tipado para los endpoints del Dashboard Builder.
 *
 * Dos superficies:
 *   - PLATAFORMA: /api/platform/dashboards/* — CRUD + preview (gate: dashboard:manage)
 *   - TENANT:     /api/dashboards/*          — datos del dashboard publicado
 *
 * Referencia: apps/worker-api/src/infrastructure/auth/dashboard-router.ts
 */
import { platformFetch } from "./platform";

// ---------------------------------------------------------------------------
// Constantes de error tipadas
// ---------------------------------------------------------------------------

export type DashboardErrorCode =
  | "SLUG_EXISTS"
  | "NAME_EXISTS"
  | "FILTER_DUPLICATE"
  | "PARAMS_UNDECLARED"
  | "PARSER_UNAVAILABLE"
  | "CACHE_TTL_INVALID"
  | "CROSS_TENANT"
  | "ORG_RESERVED";

export class DashboardApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: DashboardErrorCode | string,
  ) {
    super(message);
    this.name = "DashboardApiError";
  }

  get isRateLimit(): boolean {
    return this.status === 429;
  }
  get isParserUnavailable(): boolean {
    return this.code === "PARSER_UNAVAILABLE";
  }
}

async function toDashboardApiError(res: Response, fallback: string): Promise<DashboardApiError> {
  const payload = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
  return new DashboardApiError(res.status, payload.error ?? fallback, payload.code);
}

// ---------------------------------------------------------------------------
// Tipos — shapes REALES del worker (dashboard-router.ts)
// ---------------------------------------------------------------------------

export type DashboardStatus = "draft" | "published" | "archived";
export type WidgetType = "kpi" | "table" | "bar" | "line" | "pie" | "text";
export type FilterType = "select" | "date-range" | "text";
export type PgParamType = "text" | "int" | "numeric" | "date" | "timestamp" | "bool";

export interface DashboardListItem {
  id: string;
  slug: string;
  name: string;
  status: DashboardStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardsListResponse {
  organization: { id: string; slug: string; name: string };
  dashboards: DashboardListItem[];
}

export interface DashboardTab {
  id: string;
  dashboardId: string;
  name: string;
  sortOrder: number;
}

export interface DashboardWidget {
  id: string;
  tabId: string;
  datasourceId: string | null;
  type: WidgetType;
  title: string;
  config: Record<string, unknown> | null;
  layoutCol: number;
  layoutRow: number;
  layoutW: number;
  layoutH: number;
  sortOrder: number;
}

export interface DashboardFilter {
  id: string;
  dashboardId: string;
  tabId: string | null;
  key: string;
  label: string;
  type: FilterType;
  options: unknown | null;
  defaultValue: string | null;
  sortOrder: number;
}

export interface DatasourceParam {
  id?: string;
  paramName: string;
  pgType: PgParamType;
  required: boolean;
  defaultValue: string | null;
}

export interface Datasource {
  id: string;
  organizationId?: string;
  dashboardId: string | null;
  name: string;
  sqlText: string;
  sourceType?: string;
  cacheTtlSeconds: number;
  version: number;
  params: DatasourceParam[];
}

export interface DashboardDetail {
  dashboard: {
    id: string;
    organizationId: string;
    name: string;
    slug: string;
    status: DashboardStatus;
    version: number;
    createdAt: string;
    updatedAt: string;
  };
  tabs: DashboardTab[];
  widgets: DashboardWidget[];
  filters: DashboardFilter[];
  datasources: Datasource[];
}

/** Shape que devuelve GET /api/dashboards/:id (tenant — sin datasources) */
export interface TenantDashboardDetail {
  dashboard: {
    id: string;
    organizationId: string;
    name: string;
    slug: string;
    status: "published";
    version: number;
  };
  tabs: DashboardTab[];
  widgets: DashboardWidget[];
  filters: DashboardFilter[];
}

export interface WidgetDataResponse {
  columns: string[];
  rows: unknown[][];
  truncated: boolean;
  durationMs: number;
  message?: string;
  /** Solo si hay rate-limit (429) */
  scope?: "user" | "org";
}

// ---------------------------------------------------------------------------
// PLATAFORMA — /api/platform/dashboards/*
// ---------------------------------------------------------------------------

/** GET /api/platform/dashboards?org=:slug */
export async function fetchPlatformDashboards(orgSlug: string): Promise<DashboardsListResponse> {
  const params = new URLSearchParams({ org: orgSlug });
  const res = await platformFetch(`/api/platform/dashboards?${params.toString()}`);
  if (!res.ok) throw await toDashboardApiError(res, "Error al cargar dashboards");
  return (await res.json()) as DashboardsListResponse;
}

/** GET /api/platform/dashboards/:id */
export async function fetchPlatformDashboard(id: string): Promise<DashboardDetail> {
  const res = await platformFetch(`/api/platform/dashboards/${encodeURIComponent(id)}`);
  if (!res.ok) throw await toDashboardApiError(res, "Error al cargar el dashboard");
  return (await res.json()) as DashboardDetail;
}

/** POST /api/platform/dashboards — { organizationId, name, slug } */
export async function createPlatformDashboard(body: {
  organizationId: string;
  name: string;
  slug: string;
}): Promise<DashboardListItem & { organizationId: string }> {
  const res = await platformFetch("/api/platform/dashboards", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toDashboardApiError(res, "Error al crear dashboard");
  return (await res.json()) as DashboardListItem & { organizationId: string };
}

/** PATCH /api/platform/dashboards/:id — { name?, status? } */
export async function updatePlatformDashboard(
  id: string,
  body: { name?: string; status?: DashboardStatus },
): Promise<DashboardListItem & { organizationId: string }> {
  const res = await platformFetch(`/api/platform/dashboards/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toDashboardApiError(res, "Error al actualizar dashboard");
  return (await res.json()) as DashboardListItem & { organizationId: string };
}

/** DELETE /api/platform/dashboards/:id */
export async function deletePlatformDashboard(id: string): Promise<void> {
  const res = await platformFetch(`/api/platform/dashboards/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw await toDashboardApiError(res, "Error al eliminar dashboard");
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

/** POST /api/platform/dashboards/:id/tabs — { name, sortOrder? } */
export async function createPlatformTab(
  dashboardId: string,
  body: { name: string; sortOrder?: number },
): Promise<DashboardTab> {
  const res = await platformFetch(
    `/api/platform/dashboards/${encodeURIComponent(dashboardId)}/tabs`,
    { method: "POST", body: JSON.stringify(body) },
  );
  if (!res.ok) throw await toDashboardApiError(res, "Error al crear tab");
  return (await res.json()) as DashboardTab;
}

// ─── Widgets ─────────────────────────────────────────────────────────────────

export interface CreateWidgetBody {
  type: WidgetType;
  title: string;
  datasourceId?: string | null;
  config?: Record<string, unknown> | null;
  layoutCol?: number;
  layoutRow?: number;
  layoutW?: number;
  layoutH?: number;
  sortOrder?: number;
}

/** POST /api/platform/dashboards/:id/tabs/:tabId/widgets */
export async function createPlatformWidget(
  dashboardId: string,
  tabId: string,
  body: CreateWidgetBody,
): Promise<Pick<DashboardWidget, "id" | "tabId" | "datasourceId" | "type" | "title">> {
  const res = await platformFetch(
    `/api/platform/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}/widgets`,
    { method: "POST", body: JSON.stringify(body) },
  );
  if (!res.ok) throw await toDashboardApiError(res, "Error al crear widget");
  return (await res.json()) as Pick<
    DashboardWidget,
    "id" | "tabId" | "datasourceId" | "type" | "title"
  >;
}

// ─── Filtros ─────────────────────────────────────────────────────────────────

export interface CreateFilterBody {
  key: string;
  label: string;
  type: FilterType;
  tabId?: string | null;
  options?: unknown;
  defaultValue?: string | null;
  sortOrder?: number;
}

/** POST /api/platform/dashboards/:id/filters */
export async function createPlatformFilter(
  dashboardId: string,
  body: CreateFilterBody,
): Promise<Pick<DashboardFilter, "id" | "dashboardId" | "tabId" | "key" | "label" | "type">> {
  const res = await platformFetch(
    `/api/platform/dashboards/${encodeURIComponent(dashboardId)}/filters`,
    { method: "POST", body: JSON.stringify(body) },
  );
  if (!res.ok) throw await toDashboardApiError(res, "Error al crear filtro");
  return (await res.json()) as Pick<
    DashboardFilter,
    "id" | "dashboardId" | "tabId" | "key" | "label" | "type"
  >;
}

// ─── Datasources ─────────────────────────────────────────────────────────────

export interface CreateDatasourceBody {
  organizationId: string;
  name: string;
  sqlText: string;
  params?: DatasourceParam[];
  cacheTtlSeconds?: number;
  dashboardId?: string | null;
}

export interface UpdateDatasourceBody {
  name: string;
  sqlText: string;
  params?: DatasourceParam[];
  cacheTtlSeconds?: number;
  dashboardId?: string | null;
}

export interface DatasourceUpsertResponse extends Datasource {
  unusedParams: string[];
}

/** POST /api/platform/dashboards/:id/datasources */
export async function createPlatformDatasource(
  dashboardId: string,
  body: CreateDatasourceBody,
): Promise<DatasourceUpsertResponse> {
  const res = await platformFetch(
    `/api/platform/dashboards/${encodeURIComponent(dashboardId)}/datasources`,
    { method: "POST", body: JSON.stringify(body) },
  );
  if (!res.ok) throw await toDashboardApiError(res, "Error al crear datasource");
  return (await res.json()) as DatasourceUpsertResponse;
}

/** PATCH /api/platform/datasources/:dsId */
export async function updatePlatformDatasource(
  dsId: string,
  body: UpdateDatasourceBody,
): Promise<DatasourceUpsertResponse> {
  const res = await platformFetch(`/api/platform/datasources/${encodeURIComponent(dsId)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toDashboardApiError(res, "Error al actualizar datasource");
  return (await res.json()) as DatasourceUpsertResponse;
}

/** DELETE /api/platform/datasources/:dsId */
export async function deletePlatformDatasource(dsId: string): Promise<void> {
  const res = await platformFetch(`/api/platform/datasources/${encodeURIComponent(dsId)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw await toDashboardApiError(res, "Error al eliminar datasource");
}

// ─── Preview de datos (staff) ────────────────────────────────────────────────

/** POST /api/platform/dashboards/:id/widgets/:wid/data */
export async function previewWidgetData(
  dashboardId: string,
  widgetId: string,
  filters: Record<string, string> = {},
): Promise<WidgetDataResponse> {
  const res = await platformFetch(
    `/api/platform/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}/data`,
    { method: "POST", body: JSON.stringify({ filters }) },
  );
  if (res.status === 429) {
    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      scope?: "user" | "org";
    };
    throw new DashboardApiError(429, payload.error ?? "Rate limit exceeded", undefined);
  }
  if (!res.ok) throw await toDashboardApiError(res, "Error al previsualizar datos");
  return (await res.json()) as WidgetDataResponse;
}

// ---------------------------------------------------------------------------
// TENANT — /api/dashboards/*
// ---------------------------------------------------------------------------

/**
 * Fetch tenant (no usa platformFetch — no necesita X-Requested-With en GET).
 * Las mutaciones de widgets sí llevan CSRF.
 */
async function tenantFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    headers.set("X-Requested-With", "portal-dashboard");
    if (!headers.has("Content-Type") && init.body) {
      headers.set("Content-Type", "application/json");
    }
  }
  return fetch(`${import.meta.env.VITE_API_URL ?? "http://localhost:6787"}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
}

/** GET /api/dashboards/:id */
export async function fetchTenantDashboard(id: string): Promise<TenantDashboardDetail> {
  const res = await tenantFetch(`/api/dashboards/${encodeURIComponent(id)}`);
  if (!res.ok) throw await toDashboardApiError(res, "Error al cargar el dashboard");
  return (await res.json()) as TenantDashboardDetail;
}

/** POST /api/dashboards/:id/widgets/:wid/data */
export async function fetchTenantWidgetData(
  dashboardId: string,
  widgetId: string,
  filters: Record<string, string> = {},
): Promise<WidgetDataResponse> {
  const res = await tenantFetch(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}/data`,
    { method: "POST", body: JSON.stringify({ filters }) },
  );
  if (res.status === 429) {
    const payload = (await res.json().catch(() => ({}))) as { error?: string };
    throw new DashboardApiError(429, payload.error ?? "Rate limit exceeded");
  }
  if (!res.ok) throw await toDashboardApiError(res, "Error al cargar datos del widget");
  return (await res.json()) as WidgetDataResponse;
}
